import { createContext, useContext, useEffect, useId } from "react";

export type StructuredDataCollector = Map<string, string>;
export const StructuredDataContext = createContext<StructuredDataCollector | undefined>(undefined);

export function useStructuredData(data: Record<string, unknown> | Record<string, unknown>[]) {
  const serialized = JSON.stringify(data);
  const collector = useContext(StructuredDataContext);
  const id = useId();
  // Rendering is isolated per page; this only collects public, code-owned data.
  if (collector) collector.set(id, serialized);
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];
    const items = Array.isArray(data) ? data : [data];

    items.forEach((item, index) => {
      const key = `${id}-${index}`;
      // Adopt the build-time script instead of duplicating it on hydration.
      const existing = Array.from(document.querySelectorAll<HTMLScriptElement>("script[data-page-schema]"))
        .find((script) => script.dataset.pageSchema === key);
      const script = existing ?? document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.pageSchema = key;
      script.textContent = JSON.stringify(item);
      if (!existing) document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((script) => script.remove());
    };
    // `data` is intentionally tracked by its serialized form to avoid
    // re-running when callers pass a new-but-equivalent object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, id]);
}
